import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import * as sentry from '../../../utilities/sentry';
import SendEmailVerificationEmail from './SendEmailVerification.graphql';
import { LOGIN_PAGE, LOOM_BARE_URI, LOOM_URI } from '../../../constants/routes';
import { getGraphQLClient } from '../../../utilities/graphql';
import { STATUS_FORBIDDEN } from '../../../../../shared/constants/http';
import { VERIFIED } from '../../../../../shared/constants/userStatus';
import { getloginUrlWithRedirect } from '../../../../../shared/utilities/url';

import Button from 'components/ui-components/button';
import Modal from 'components/ui-components/modal';

import './styles.less';

/*
 * This component is responsible for 3 code paths:
 * 1. The viewer is trying to view a video that has email gating and they are not
 *    in this list.
 * 2. The viewer is trying to view a video that has email gating and they are logged
 *    out.
 * 3. The viewer is trying to view a video that has email gating, they are in this
 *    list, but their email is not verified.
 *
 * We always surface this UI from path #3 if the user does not have their email
 * verified in an attempt to get them to verify even if they are not in the list
 * of permitted viewers.
 *
 * It is used in 2 places:
 * 1. At the top-level of the share video page to gate all page interaction.
 * 2. Within the embed video player in case the video is embedded on an external
 *    site
 */

const LoggedOutScreen = ({ redirect, isMainVideoOnSharePage, videoId }) => {
  const modalBody = isMainVideoOnSharePage
    ? 'Public viewing has been disabled by the video owner. Login to view or request access.'
    : `Log in on ${LOOM_BARE_URI} for access`;

  const modalAction = isMainVideoOnSharePage ? (
    <Fragment>
      <Button
        onClicked={() => {
          window.location = getloginUrlWithRedirect(
            redirect,
            'Please Login to view this video'
          );
        }}
        capitalize={false}
        buttonType="rp-primary-2"
        value="Login"
      />
      <a href={`${LOGIN_PAGE}?signup=1`} className="secondary-cta">
        Create an Account
      </a>
    </Fragment>
  ) : (
    <a
      rel="noopener noreferrer"
      target="_blank"
      href={`${LOOM_URI}/share/${videoId}`}
    >
      <Button
        capitalize={false}
        buttonType="rp-primary-3"
        value="Go to Video"
      />
    </a>
  );

  return (
    <Fragment>
      <div className="loom-permissions-modal-title">
        You need permission to view this video
      </div>
      <div className="loom-permissions-modal-body">{modalBody}</div>
      {modalAction}
    </Fragment>
  );
};

const ViewerNotInEmailWhitelistScreen = ({
  error,
  onClick,
  requestSent,
  alreadyPinged,
}) => {
  return (
    <Fragment>
      {!requestSent && (
        <Fragment>
          <div className="loom-permissions-modal-title">
            You need permission to view this video
          </div>
          <div className="loom-permissions-modal-body">
            Public viewing has been disabled by the video owner.
          </div>
          <Button
            onClicked={onClick}
            capitalize={false}
            buttonType="rp-primary-2"
            value="Request access"
          />
        </Fragment>
      )}
      {requestSent && (
        <div className="loom-permissions-modal-title">
          {!alreadyPinged && 'Your request has been sent!'}
          {alreadyPinged &&
            "You're only allowed to request access once a day per video."}
        </div>
      )}
      {error && <div className="loom-permissions-modal-title">{error}</div>}
    </Fragment>
  );
};

const ViewerNeedsEmailVerificationScreen = ({
  email,
  error,
  onClick,
  requestSent,
}) => (
  <Fragment>
    {!requestSent && (
      <Fragment>
        <div className="loom-permissions-modal-title">
          You need permission to view this video
        </div>
        <div className="loom-permissions-modal-body">
          Public viewing has been disabled by the video owner. Verify your email
          ({email}) to view.
        </div>
        <Button
          onClicked={onClick}
          capitalize={false}
          buttonType="rp-primary-2"
          value="Resend Verify Email"
        />
      </Fragment>
    )}
    {requestSent && (
      <div className="loom-permissions-modal-title">
        Your verification email was sent!
      </div>
    )}
    {error && <div className="loom-permissions-modal-title">{error}</div>}
  </Fragment>
);

class PermissionModal extends Component {
  state = { showSignup: false, requestSent: false, alreadyPinged: false };

  render() {
    const {
      email,
      error,
      videoId,
      isLoggedIn,
      loggedInChecked,
      isMainVideoOnSharePage,
      userHasEmailVerification,
    } = this.props;

    const { requestSent, alreadyPinged } = this.state;

    if (!loggedInChecked) {
      return null;
    }

    return (
      <div className="loom-video-permission-modal-wrapper">
        <Modal
          show={true}
          showClose={false}
          dark={true}
          className="loom-video-modal"
        >
          {!isLoggedIn && (
            <LoggedOutScreen
              redirect={this.getRedirectString(videoId)}
              isMainVideoOnSharePage={isMainVideoOnSharePage}
              videoId={videoId}
            />
          )}
          {isLoggedIn && !userHasEmailVerification && (
            <ViewerNeedsEmailVerificationScreen
              email={email}
              error={error}
              onClick={this.sendEmailVerificationEmail}
              requestSent={requestSent}
            />
          )}
          {isLoggedIn && userHasEmailVerification && (
            <ViewerNotInEmailWhitelistScreen
              error={error}
              onClick={this.pingVideoOwnerForAccess}
              requestSent={requestSent}
              alreadyPinged={alreadyPinged}
            />
          )}
        </Modal>
      </div>
    );
  }

  getRedirectString = videoId => `${LOOM_URI}/share/${videoId}`;

  showSignupModal = () => this.setState({ showSignup: true });

  pingVideoOwnerForAccess = async () => {
    try {
      this.setState({ error: null });

      const { videoId } = this.props;

      const resp = await fetch(`/v1/videos/${videoId}/access_ping`, {
        credentials: 'include',
        method: 'POST',
      });

      if (resp.status === STATUS_FORBIDDEN) {
        this.setState({ alreadyPinged: true, requestSent: true });

        return;
      } else if (resp.status >= 400) {
        throw new Error(
          `Error checking password - HTTP response: ${resp.status}`
        );
      }

      this.setState({ requestSent: true });
    } catch (err) {
      sentry.logException(err, 'error requesting access for your video');
      this.setState({
        error: 'There was an error requesting access for your video',
      });
    }
  };

  sendEmailVerificationEmail = async () => {
    try {
      this.setState({ error: null });

      const client = getGraphQLClient();

      await client.mutate({ mutation: SendEmailVerificationEmail });

      this.setState({ requestSent: true });
    } catch (err) {
      sentry.logException(err, 'error sending email verification email');
      this.setState({
        error: 'There was an error sending your email verification email',
      });
    }
  };
}

const mapStateToProps = state => {
  const {
    current: user,
    is_logged_in: isLoggedIn,
    is_logged_in_checked: loggedInChecked,
  } = state.user;

  const {
    isMainVideoOnSharePage,
    video: { id: videoId },
  } = state.video_player;

  const userHasEmailVerification = user && user.status === VERIFIED;
  const email = user && user.email;

  return {
    email,
    videoId,
    isLoggedIn,
    loggedInChecked,
    isMainVideoOnSharePage,
    userHasEmailVerification,
  };
};

export default connect(mapStateToProps, null)(PermissionModal);
